/**
 * PlaceholderList - Dropdown component for placeholder autocomplete suggestions
 * 
 * Renders a polished, shadcn-inspired list of available placeholders 
 * when the user types a trigger character.
 * Supports keyboard navigation and selection.
 */

import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
  useCallback,
  useRef,
} from 'react';
import PlaceholderChip from '../common/PlaceholderChip';
import './TipTapEditor.css';

const PlaceholderList = forwardRef(({ items, command, triggerChar }, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const containerRef = useRef(null);
  const itemRefs = useRef([]);

  const selectItem = useCallback((index) => {
    const item = items[index];
    if (item && command) {
      command(item);
    }
  }, [items, command]);

  const upHandler = useCallback(() => {
    setSelectedIndex((prev) => 
      (prev + items.length - 1) % items.length
    );
  }, [items.length]);

  const downHandler = useCallback(() => {
    setSelectedIndex((prev) => 
      (prev + 1) % items.length
    );
  }, [items.length]);

  const enterHandler = useCallback(() => {
    selectItem(selectedIndex);
  }, [selectItem, selectedIndex]);

  // Reset selection when items change
  useEffect(() => {
    setSelectedIndex(0);
    itemRefs.current = [];
  }, [items]);

  // Scroll selected item into view smoothly
  useEffect(() => {
    const selectedElement = itemRefs.current[selectedIndex];
    if (selectedElement) {
      selectedElement.scrollIntoView({ 
        block: 'nearest', 
        behavior: 'smooth' 
      });
    }
  }, [selectedIndex]);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === 'ArrowUp') {
        upHandler();
        return true;
      }

      if (event.key === 'ArrowDown') {
        downHandler();
        return true;
      }

      if (event.key === 'Enter') {
        enterHandler();
        return true;
      }

      return false;
    },
  }));

  if (!items || items.length === 0) {
    return (
      <div className="placeholder-suggestion-popup">
        <div className="placeholder-list-container">
          <div className="placeholder-list-empty">
            No matching placeholders
          </div>
        </div>
      </div>
    );
  }

  const trigger = triggerChar || '$';
  const isAgentMention = trigger === '@';

  return (
    <div className="placeholder-suggestion-popup">
      <div className="placeholder-list-container" ref={containerRef}>
        {items.map((item, index) => {
          const isSelected = index === selectedIndex;

          return (
            <button
              key={`${item.id || item.key}-${index}`}
              ref={(el) => (itemRefs.current[index] = el)}
              className={`placeholder-list-item ${isSelected ? 'selected' : ''}`}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); selectItem(index); }}
              onMouseEnter={() => setSelectedIndex(index)}
              onMouseDown={(e) => e.preventDefault()}
              type="button"
            >
              <PlaceholderChip
                placeholderKey={item.key}
                prefix={isAgentMention ? '@' : trigger}
                type={item.type}
                length={item.length}
                size="small"
              />
              {(isAgentMention ? item.description : item.type) && (
                <span className="placeholder-list-item-description">
                  {isAgentMention
                    ? (item.description.length > 40 ? item.description.slice(0, 40) + '…' : item.description)
                    : item.type}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
});

PlaceholderList.displayName = 'PlaceholderList';

export default PlaceholderList;
